import React, { useState, useEffect, useCallback } from 'react';
import { fetchWatch2Data as fetchWatch2Api } from '../services/api';

/**
 * 감시2 탭 컴포넌트
 * HAITIAN 사출기 감시2 화면을 구현 - 샷 이력 테이블
 */
const MonitoringWatch2 = ({ equipmentId, craftId, machineData }) => {
  const [startNumber, setStartNumber] = useState(1);
  const [sampleInterval, setSampleInterval] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 테이블 컬럼 정의 (DB 매핑)
  const columns = [
    { key: 'no', name: 'No.', unit: '' },
    { key: 'shotCount', name: '샷카운트', unit: '' },
    { key: 'cycleTime', name: '사이클타임', unit: 'sec' },
    { key: 'injHoldTime', name: 'Inj. Hold. Time', unit: 'sec' },
    { key: 'chargeTime', name: '계량시간', unit: 'sec', important: true },
    { key: 'injStartPosi', name: '사출시작', unit: 'mm' },
    { key: 'turnPosi', name: '보압시작', unit: 'mm' },
    { key: 'injEndPosi', name: '쿠션량', unit: 'mm', important: true },
    { key: 'fetchTime', name: '취출시간', unit: 'sec' }
  ];

  // API 데이터 조회
  const fetchWatch2Data = useCallback(async () => {
    console.log('Watch2 fetchWatch2Data called with equipmentId:', equipmentId);
    if (!equipmentId) {
      console.warn('Watch2: equipmentId가 없습니다');
      return;
    }

    setLoading(true);
    try {
      const data = await fetchWatch2Api(equipmentId);
      console.log('Watch2 API response:', data);
      if (data.success && data.data?.shots) {
        setTableData(data.data.shots);
      }
    } catch (error) {
      console.error('감시2 데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  // 초기 로딩 및 주기적 갱신
  useEffect(() => {
    fetchWatch2Data();

    // 10초마다 갱신
    const interval = setInterval(fetchWatch2Data, 10000);
    return () => clearInterval(interval);
  }, [fetchWatch2Data]);

  const getColWidth = (colKey) => {
    if (colKey === 'no') return '5%';
    return `${95 / (columns.length - 1)}%`;
  };

  // 시작번호, 샘플간격 적용한 필터링 데이터
  const filteredData = tableData.filter((_, idx) => {
    const rowNum = idx + 1;
    if (rowNum < startNumber) return false;
    const interval = Math.max(1, sampleInterval);
    return (rowNum - startNumber) % interval === 0;
  });

  const getValue = (row, colKey, rowIdx) => {
    if (colKey === 'no') return rowIdx + 1;
    if (colKey === 'injHoldTime') {
      // Inj. Hold. Time = hold_time_1 컬럼
      return row.holdTime1;
    }
    return row[colKey];
  };

  const formatValue = (value, colKey) => {
    if (value === null || value === undefined) return '-';
    if (colKey === 'no') return value;
    if (colKey === 'shotCount') return value.toLocaleString();
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      height: '100%'
    }}>
      {/* 설정 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '4px 12px'
      }}>
        <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px' }}>시작번호:</label>
            <input
              type="number"
              min={1}
              value={startNumber}
              onChange={(e) => setStartNumber(Math.max(1, Number(e.target.value)))}
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#E5E7EB',
                fontSize: '13px',
                width: '60px',
                textAlign: 'center'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px' }}>샘플간격:</label>
            <input
              type="number"
              min={1}
              value={sampleInterval}
              onChange={(e) => setSampleInterval(Math.max(1, Number(e.target.value)))}
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#E5E7EB',
                fontSize: '13px',
                width: '60px',
                textAlign: 'center'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ color: '#9CA3AF', fontSize: '13px' }}>리셋:</label>
            <select
              value="미사용"
              disabled
              style={{
                padding: '6px 10px',
                backgroundColor: '#1B1D20',
                border: '1px solid #374151',
                borderRadius: '4px',
                color: '#6B7280',
                fontSize: '13px',
                cursor: 'not-allowed'
              }}
            >
              <option value="미사용">미사용</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', color: '#6B7280', fontSize: '12px' }}>
            {loading ? '로딩 중...' : `${filteredData.length}개 표시 / 총 ${tableData.length}개 샷`}
          </div>
        </div>
      </div>

      {/* 테이블 카드 */}
      <div style={{
        backgroundColor: '#21252b',
        border: '1px solid #1B1D20',
        padding: '12px',
        flex: 1,
        overflow: 'hidden'
      }}>
        <div style={{ overflowY: 'auto', height: '100%' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '3px 0',
            fontSize: '13px',
            tableLayout: 'fixed'
          }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{
                    padding: '8px 6px',
                    backgroundColor: '#1B1D20',
                    color: '#FFFFFF',
                    fontWeight: '600',
                    borderTop: col.important ? '2px dashed #F472B6' : 'none',
                    borderLeft: col.important ? '2px dashed #F472B6' : 'none',
                    borderRight: col.important ? '2px dashed #F472B6' : 'none',
                    textAlign: 'center',
                    width: getColWidth(col.key),
                    whiteSpace: 'nowrap'
                  }}>
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6B7280'
                  }}>
                    {loading ? '데이터 로딩 중...' : '데이터가 없습니다'}
                  </td>
                </tr>
              ) : (
                filteredData.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {columns.map((col, colIdx) => {
                      const value = getValue(row, col.key, rowIdx);
                      const isLastRow = rowIdx === filteredData.length - 1;
                      const isEvenRow = rowIdx % 2 === 0;

                      return (
                        <td key={colIdx} style={{
                          padding: '6px',
                          color: '#FFFFFF',
                          backgroundColor: isEvenRow ? '#2a2d32' : '#252830',
                          borderBottom: col.important && isLastRow ? '2px dashed #F472B6' : '1px solid #1B1D20',
                          borderLeft: col.important ? '2px dashed #F472B6' : 'none',
                          borderRight: col.important ? '2px dashed #F472B6' : 'none',
                          textAlign: 'center'
                        }}>
                          {formatValue(value, col.key)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonitoringWatch2;
